<template>
    <div class="p-3">
        <h4 class="text-dark">Control Center: Organization</h4>

        <h5 class="text-dark mt-5">General</h5>
        <div class="w-100 d-flex justify-content-end">
            <a class="font-weight-bold link-primary text-primary" @click="editOrganizationInfo()"
                >Edit</a
            >
        </div>
        <section class="mt-3">
            <div class="form-group row">
                <label for="organization_logo" class="col-sm-4 col-form-label font-weight-bold"
                    >Organization logo:</label
                >
                <div class="col-3">
                    <div class="bg-white position-relative">
                        <span
                            class="position-absolute h-100 w-100 d-flex justify-content-center align-items-center hover-reveal cursor-pointer"
                            @click="() => (isCropperActive = !isCropperActive)"
                            >Change</span
                        >
                        <img
                            class="position-relative img-fluid profile-image hover-50"
                            :src="
                                organization.logo_path
                                    ? organization.logo_path.replace('public', '/storage')
                                    : '../../../images/default_organization.jpeg'
                            "
                        />
                    </div>
                    <image-cropper
                        v-model="isCropperActive"
                        :url="`/api/organizations/${organization.id}/profile_photo`"
                        field="file"
                        langType="en"
                        :width="300"
                        :height="300"
                        @crop-upload-success="handlePhotoUploadSuccess"
                        @crop-upload-fail="handlePhotoUploadFailure"
                        noCircle
                    />
                </div>
            </div>
        </section>
        <section class="mt-3">
            <form>
                <div class="form-group row">
                    <label for="organization_name" class="col-sm-4 col-form-label font-weight-bold"
                        >Organization name:</label
                    >
                    <div class="col-sm-8">
                        <input
                            type="text"
                            :disabled="!editOrganization"
                            class="form-control"
                            id="organization_name"
                            placeholder="Organization name"
                            v-model="organization_.name"
                        />
                    </div>
                </div>

                <div class="form-group row">
                    <label for="tax_id" class="col-sm-4 col-form-label font-weight-bold"
                        >Tax ID:</label
                    >
                    <div class="col-sm-8">
                        <input
                            type="text"
                            :disabled="!editOrganization"
                            class="form-control"
                            id="tax_id"
                            placeholder="Tax ID"
                            v-model="organization_.tax_id_number"
                        />
                    </div>
                </div>

                <div class="form-group row">
                    <label for="npi" class="col-sm-4 col-form-label font-weight-bold"
                        >NPI (National Provider Identifier):</label
                    >
                    <div class="col-sm-8">
                        <input
                            type="text"
                            :disabled="!editOrganization"
                            class="form-control"
                            id="npi"
                            placeholder="National Provider Identifier #"
                            v-model="organization_.npi_number"
                        />
                    </div>
                </div>

                <div class="form-group row">
                    <label for="taxonomy_code" class="col-sm-4 col-form-label font-weight-bold"
                        >Taxonomy Code:</label
                    >
                    <div class="col-sm-8">
                        <input
                            type="text"
                            :disabled="!editOrganization"
                            class="form-control"
                            id="taxonomy_code"
                            placeholder="Taxonomy Code"
                            v-model="organization_.taxonomy_code"
                        />
                    </div>
                </div>

                <div class="d-flex justify-content-end">
                    <button
                        id="save-info"
                        :disabled="!editOrganization"
                        :class="editOrganization ? `btn-primary` : `btn-light cursor-not-allowed`"
                        class="btn btn-sm text-white font-weight-bold text-uppercase"
                        @click.prevent="editOrganization ? updateSettings(`save-info`) : null"
                    >
                        Save
                    </button>
                </div>
            </form>
        </section>

        <template v-if="['professional_platinum'].includes(organization.type)">
            <h5 class="text-dark mt-5">Stripe Settings</h5>
            <div class="w-100 d-flex justify-content-between">
                <span>
                    <i class="ml-2 fa fa-info-circle text-info"></i>
                    <button
                        @click.prevent="isStripeInfoVisible = true"
                        class="btn btn-sm btn-link small btn-link"
                    >
                        More information on setting up Stripe
                    </button>
                </span>
                <a class="font-weight-bold link-primary text-primary" @click="editStripeSettings()"
                    >Edit</a
                >
            </div>

            <section class="mt-3">
                <form>
                    <div class="form-group row">
                        <label for="stripe_key" class="col-sm-4 col-form-label font-weight-bold"
                            >Stripe key:</label
                        >
                        <div class="col-sm-8">
                            <input
                                type="text"
                                :disabled="!editStripe"
                                class="form-control"
                                id="stripe_key"
                                placeholder="Stripe key"
                                v-model="organization_.stripe_key"
                            />
                            <span
                                v-if="
                                    helpers._get(organization_, 'stripe_key', '') &&
                                        helpers
                                            ._get(organization_, 'stripe_key', '')
                                            .includes('test')
                                "
                            >
                                <i
                                    class="mr-2 fa fa-exclamation-triangle fa-warning text-warning"
                                ></i>
                                <span class="small font-italic">
                                    Looks like you entered a test key. Billing functionality needs a
                                    live key to work properly.
                                    <button
                                        @click.prevent="isStripeInfoVisible = true"
                                        class="btn btn-link btn-sm"
                                    >
                                        More info
                                    </button></span
                                >
                            </span>
                            <span v-else="">
                                &nbsp;
                            </span>
                        </div>
                    </div>

                    <div class="form-group row">
                        <label for="stripe_secret" class="col-sm-4 col-form-label font-weight-bold"
                            >Stripe secret:</label
                        >
                        <div class="col-sm-8">
                            <input
                                type="text"
                                :disabled="!editStripe"
                                class="form-control"
                                id="stripe_secret"
                                placeholder="Stripe secret"
                                v-model="organization_.stripe_secret"
                            />
                            <span
                                v-if="
                                    helpers._get(organization_, 'stripe_secret', '') &&
                                        helpers
                                            ._get(organization_, 'stripe_secret', '')
                                            .includes('test')
                                "
                            >
                                <i
                                    class="mr-2 fa fa-exclamation-triangle fa-warning text-warning"
                                ></i>
                                <span class="small font-italic">
                                    Looks like you entered a test key. Billing functionality needs a
                                    live key to work properly.
                                    <button
                                        @click.prevent="isStripeInfoVisible = true"
                                        class="btn btn-link btn-sm"
                                    >
                                        More info
                                    </button></span
                                >
                            </span>
                            <span v-else="">
                                &nbsp;
                            </span>
                        </div>
                    </div>
                    <div class="d-flex justify-content-end">
                        <button
                            id="save-settings"
                            :disabled="!editStripe"
                            :class="editStripe ? `btn-primary` : `btn-light cursor-not-allowed`"
                            class="btn btn-sm text-white font-weight-bold text-uppercase"
                            @click.prevent="editStripe ? updateSettings(`save-settings`) : null"
                        >
                            Save
                        </button>
                    </div>
                </form>
                <modal-component
                    id="stripe-info"
                    size="lg"
                    :visible="isStripeInfoVisible"
                    @close="isStripeInfoVisible = false"
                >
                    <div class="p-3">
                        <h5 class="text-dark text-uppercase">Setting up Stripe billing</h5>

                        <p>
                            Make sure to enter your company's live keys in both fields. <br />
                            In your Stripe dashboard, under the API keys menu entry, the 'view test
                            data' toggle should be <b>off</b>.
                        </p>

                        <div class="d-flex justify-content-center align-items-center">
                            <img
                                src="../../../images/stripe_help.png"
                                alt="Stripe screenshot"
                                class="img-fluid"
                            />
                        </div>
                    </div>
                </modal-component>
            </section>
        </template>

        <div class="my-2">
            <div v-if="success" class="alert alert-success">
                <a href="#" class="close" data-dismiss="alert">&times;</a>
                <span>Your changes have been saved.</span>
            </div>
            <errors :errors="errors"></errors>
        </div>

        <section>
            <manage-company-documents :organization="organization"></manage-company-documents>
        </section>
    </div>
</template>

<script>
import { helpers } from "../../plugins/helpers";
import { errors } from "../../plugins/errors";
import ImageCropper from "../widgets/ImageCropper";
import ManageCompanyDocuments from "./ManageCompanyDocuments";
import ModalComponent from "../ui/ModalComponent";

export default {
    name: "AdminOrganization",
    props: ["organization"],
    mixins: [errors],
    components: {
        ManageCompanyDocuments,
        ModalComponent,
        ImageCropper,
    },
    data() {
        return {
            helpers,
            organization_: {},
            success: false,
            editStripe: false,
            editOrganization: false,
            isCropperActive: false,
            isStripeInfoVisible: false,
        };
    },
    created() {
        this.initialize();
    },

    methods: {
        initialize() {
            this.getOrganization();
        },

        getOrganization() {
            axios.get(`/api/organizations/${this.organization.id}`).then(r => {
                this.organization_ = r.data.data;
            });
        },

        editStripeSettings() {
            this.editStripe = true;
            this.organization_.stripe_secret = null;
        },

        editOrganizationInfo() {
            this.editOrganization = true;
        },

        updateSettings(buttonId) {
            helpers.toggleSpinner(buttonId, "Saving...");
            this.setErrors();
            this.success = false;
            let payload = {};

            if (this.editStripe) {
                payload.stripe_key = this.organization_.stripe_key;
                payload.stripe_secret = this.organization_.stripe_secret;
            }

            if (this.editOrganization) {
                payload.name = this.organization_.name;
                payload.tax_id_number = this.organization_.tax_id_number;
                payload.npi_number = this.organization_.npi_number;
                payload.taxonomy_code = this.organization_.taxonomy_code;
            }

            axios
                .patch(`/api/organizations/${this.organization.id}`, payload)
                .then(r => {
                    this.success = true;
                    this.setErrors();
                    this.getOrganization();
                    this.editStripe = false;
                    this.editOrganization = false;
                    helpers.toggleSpinner(buttonId, "Saving...");
                })
                .catch(e => {
                    console.error(e);
                    this.success = false;
                    let errorObject = _.get(e, "response.data.errors", {});
                    let errorDetails = "";
                    Object.keys(errorObject).forEach(
                        errorKey => (errorDetails += `${errorObject[errorKey]}<br>`)
                    );
                    this.$swal({
                        title: "Error",
                        html: `An error occurred while saving. <br>${errorDetails}`,
                        type: "error",
                        confirmButtonText: "Close",
                        confirmButtonColor: "#d64571",
                    });
                    this.editStripe = false;
                    this.editOrganization = false;
                    helpers.toggleSpinner(buttonId, "Saving...");
                });
        },

        handlePhotoUploadSuccess({ status, image }) {
            this.organization.logo_path = image;
        },
        handlePhotoUploadFailure(err) {
            console.error(err);
        },
    },
};
</script>

<style scoped lang="scss">
.hover-reveal {
    transition: all 0.3s;
    z-index: 2;
    opacity: 0;
}
.hover-reveal:hover {
    opacity: 1;
}

.hover-reveal:hover + .hover-50 {
    transition: all 0.3s;
    opacity: 0.5;
}
</style>
